import { getAdminModuleMockedUser, setAdminModuleAccessToken } from '@sodexo/admin-module-react';
import { QueryClient } from '@tanstack/react-query';
import { AppInsights } from '../Components/AppInsightHOC';
import { msalInstance } from '../msalInstance';
import { useStore } from '../store/store';

export class FetchService {
  private static headers: { authorization: string };

  private static ws: WebSocket | null = null;
  private static webSocketListeners: WebSocketEventListener[] = [];
  private static creatingWebSocket = false;

  static generateToken({ forceRefresh }: { forceRefresh?: boolean } = {}) {
    const account = msalInstance.getActiveAccount();
    if (account) {
      // We need to specify a scope to make the cache works: https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/6451#issuecomment-1731143422
      try {
        return msalInstance.acquireTokenSilent({ scopes: ['User.Read'], forceRefresh, account });
      } catch (e) {
        console.log(e);
        return msalInstance.acquireTokenPopup({
          scopes: ['User.Read'],
          loginHint: msalInstance.getActiveAccount()?.username,
        });
      }
    }
    return undefined;
  }

  private static async refreshToken() {
    const token = await FetchService.generateToken();
    if (token?.accessToken) {
      setAdminModuleAccessToken(token?.accessToken);
    }
    FetchService.headers = {
      authorization: `Bearer ${token?.accessToken}`,
      ...((!token || !token.accessToken) && { 'x-api-key': 'd8a340fd-01af-462e-b9f4-ce3ae485dd54' }),
      ...(getAdminModuleMockedUser() && { 'sdx-admin-module-user-mock': getAdminModuleMockedUser() }),
    };
  }

  static async rawFetch(path: string, options?: RequestInit): Promise<Response> {
    return (await FetchService.performFetchRequest(path, options, true)) as Response;
  }

  static async get<T>(path: string, options?: RequestInit): Promise<T> {
    return (await FetchService.performFetchRequest<T>(path, options)) as Promise<T>;
  }

  static async rawPost<Body = unknown>(path: string, body?: Body, options?: RequestInit): Promise<Response> {
    return (await FetchService.performFetchRequest(
      path,
      {
        ...options,
        method: 'POST',
        body: JSON.stringify(body),
      },
      true,
    )) as Response;
  }

  static async post<Response, Body = unknown>(path: string, body?: Body, options?: RequestInit) {
    return (await FetchService.performFetchRequest<Response>(path, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    })) as Promise<Response>;
  }

  static async put<Response, Body = unknown>(path: string, body?: Body) {
    return (await FetchService.performFetchRequest<Response>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    })) as Promise<Response>;
  }

  static async delete<Response, Body = unknown>(path: string, body?: Body) {
    return (await FetchService.performFetchRequest<Response>(path, {
      method: 'DELETE',
      body: JSON.stringify(body),
    })) as Promise<Response>;
  }

  static async addWebSocketListener(listener: Omit<WebSocketEventListener, 'id'>) {
    await FetchService.connectWebsocket();
    const id = Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    FetchService.webSocketListeners.push({ type: listener.type, listener: listener.listener as any, id });
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    FetchService.ws?.addEventListener(listener.type, listener.listener as any);
    return id;
  }

  static async removeWebSocketListener(id: string) {
    const index = FetchService.webSocketListeners.findIndex(l => l.id === id);
    const listener = FetchService.webSocketListeners[index];
    FetchService.webSocketListeners.splice(index, 1);
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    FetchService.ws?.removeEventListener(listener.type, listener.listener as any);
  }

  private static async performFetchRequest<T>(
    path: string,
    options: RequestInit = {},
    returnRawResponse = false,
  ): Promise<T | Response | undefined> {
    await FetchService.refreshToken();
    const catalog = useStore.getState().catalog;
    const rawResponse = await fetch(`${process.env.BACKEND_URL}/api${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(catalog ? { 'x-catalog': JSON.stringify(catalog.toCatalogInHeaders()) } : {}),
        ...options?.headers,
        ...FetchService.headers,
      },
    });

    if (!rawResponse.ok) {
      let data: T | undefined;
      let message: string | undefined;
      try {
        data = await rawResponse.json();
      } catch (e) {
        try {
          message = await rawResponse.text();
        } catch (e) {
          message = rawResponse.statusText;
        }
      }
      const error = new FetchError(rawResponse, message, data);
      AppInsights?.trackException({
        exception: error,
        properties: {
          path,
          options: JSON.stringify(options),
          url: window.location.href,
        },
      });
      throw error;
    }

    if (returnRawResponse) {
      return rawResponse;
    }
    try {
      return (await rawResponse.json()) as Promise<T>;
    } catch (e) {
      return undefined;
    }
  }

  private static async connectWebsocket() {
    if (FetchService.creatingWebSocket || (FetchService.ws && !FetchService.ws.CLOSED)) return;
    FetchService.creatingWebSocket = true;

    const onClose = () => {
      setTimeout(() => {
        FetchService.connectWebsocket();
      }, 5000);
    };

    const onError = (event: Event) => {
      FetchService.ws?.close();
    };

    const token = await FetchService.generateToken();
    if (!token) throw new Error('Cannot init websocket, token is not available');
    FetchService.ws = new WebSocket(`${process.env.BACKEND_URL}/api?token=${token.accessToken}`);
    FetchService.webSocketListeners.forEach(listener =>
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      FetchService.ws?.addEventListener(listener.type, listener.listener as any),
    );

    FetchService.ws.addEventListener('close', onClose);
    FetchService.ws.addEventListener('error', onError);

    FetchService.creatingWebSocket = false;
  }
}

export class FetchError<T = unknown> extends Error {
  constructor(
    public res: Response,
    message?: string,
    public data?: T,
  ) {
    super(message);
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if ([403, 404, 409].includes(error.res.status)) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
    },
  },
});

type WebSocketEventListener = {
  [K in keyof WebSocketEventMap]: {
    id: string;
    type: K;
    listener: (event: WebSocketEventMap[K]) => void;
  };
}[keyof WebSocketEventMap];
